import React, { useEffect, useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'

import { fechaIsoToFull } from '../utils/utils'
import Message from '../interfaces/Message'
import useMessagePoster from '../hooks/useMessagePoster'
import { useAuth } from '../hooks/useAuth'
import { UserProfileDto } from '../modules/users/types/UserProfileDto'
import useUserProfileFetcher from '../modules/users/hooks/useUserProfileFetcher'
import DocToHeader from '../interfaces/DocInactive'
import { DocStateSpecial } from '../enums/DocStates'
import { markMessageAsRead } from '../api/messages'

import { DocStatusHeader } from './DocStatusHeader'
import SelectUserField from './SelectUserField'
import { ButtonAction, ButtonMenuSet } from './Buttons'
import { FormFields, InputFieldProps } from './FormFields'
import CheckboxField from './CheckBoxField'
import { DisplayValueFormat } from './DisplayValue'

import { AppPath } from '@/enums/AppPath'

const MessageDetail: React.FC<{
  message: Message
  onRefresh: () => void
}> = ({ message, onRefresh }) => {
  const [formData, setFormData] = useState<Message>(message)
  const [disabled, setDisabled] = useState<boolean>(true)
  const [clickedIndex, setClickedIndex] = useState<number>(-1)
  const [users, setUsers] = useState<UserProfileDto[]>([])
  const { userData } = useAuth()
  const { fetchUserProfiles, errorUserProfile } = useUserProfileFetcher()
  const { postMessageEdit, errorPostMessage } = useMessagePoster()

  const captionAsignar = (message.assignedTo_user ?? '').trim().length === 0 ? 'Asignar' : ''

  const buttonCaptions = [captionAsignar, 'Procesar', 'Descartar']

  let msgDescartado: DocToHeader = {
    state: DocStateSpecial.Activo,
    date: '',
    cause: '',
    by: '',
  }

  if (message.discardedAt) {
    msgDescartado = {
      state: DocStateSpecial.Descartado,
      date: message.discardedAt ?? '',
      cause: '',
      by: '',
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setDisabled(false)

    if (!formData) {
      return
    }

    // Handle nested fields such as 'profile.email'
    const keys = name.split('.')

    if (keys.length > 1) {
      // For nested fields, like 'profile.email'
      // Check if the property is an object
      const firstKey = keys[0] as keyof Message
      const secondKey = keys[1]
      // Ensure prevState[firstKey] is an object before spreading
      const nestedObject = formData[firstKey]

      if (typeof nestedObject === 'object' && nestedObject !== null) {
        setFormData({
          ...formData,
          [firstKey]: {
            ...nestedObject, // Safely spread only if it's an object
            [secondKey]: value,
          },
        })
      } else {
        setFormData({
          ...formData,
          [firstKey]: {
            [secondKey]: value,
          },
        })
      }
    } else {
      setFormData({
        ...formData,
        [name]: value,
      })
    }
  }

  const fields: Array<InputFieldProps> = [
    { label: 'Comercio', name: 'external', value: formData?.external ?? '', type: 'string' },
    { label: 'Vehículo', name: 'vehicle', value: formData?.vehicle ?? '', type: 'string' },
    { label: 'Remitente', name: 'contact.name', value: formData?.contact?.name ?? '', type: 'string' },
    { label: 'Teléfono', name: 'contact.phone', value: formData?.contact?.phone ?? '', type: 'string' },
    { label: 'Email', name: 'contact.email', value: formData?.contact?.email ?? '', type: 'string' },
    { label: 'Anuncio', name: 'adUrl', value: formData?.adUrl ?? '', type: 'string' },
    { label: 'Fecha Descarte', name: 'discardedAt', value: formData?.discardedAt ?? '', type: 'string' },
  ]

  // Handle checkbox change
  const handleCheckClick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked, name } = e.target

    if (!formData) return
    setDisabled(false)
    setFormData((prevFormData) => {
      return {
        ...prevFormData,
        [name]: checked,
      }
    })
  }

  const onModifyClick = async () => {
    if (disabled) return

    await postMessageEdit(formData)
  }
  const handleMenuClick = async (index: number) => {
    setClickedIndex(index)

    if (index === 0) {
      if (!userData) return
      const organizationId = userData?.organization?._id ?? ''
      // Invcar fetch XXXX
      const data: UserProfileDto[] = await fetchUserProfiles(organizationId, 0)
      setUsers(data)
      // guardar en un nuevo estado por ejemplo users lo recibido
    }
  }

  useEffect(() => {
    // Scroll al final
    if (clickedIndex >= 0) {
      window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' })
    }
  }, [clickedIndex])

  useEffect(() => {
    // Mark message as read on mount
    if (message._id && !message.readAt) {
      void markMessageAsRead(message._id)
    }
  }, [message._id, message.readAt])

  const onRefreshEx = () => {
    void handleMenuClick(-1)

    onRefresh()
  }

  const decodeHtmlEntities = (encodedString: string): string => {
    const parser = new DOMParser()
    const doc = parser.parseFromString(encodedString, 'text/html')

    return doc.documentElement.textContent ?? ''
  }

  return (
    <div>
      <DocStatusHeader docName="mensaje" state={msgDescartado} />
      <div className="text-xl font-bold">Detalles del mensaje</div>
      <div className="grid grid-cols-1 sm:grid-cols-[auto,1fr] mb-4 gap-x-20 ">
        <DisplayValueFormat label="De" value={message.from} />
        <DisplayValueFormat label="Asunto" value={message.subject} />
        <DisplayValueFormat label="Recibido" value={fechaIsoToFull(message.createdAt)} />
        <DisplayValueFormat label="Asignado a" value={message.assignedTo_user} />
      </div>

      <div className=" mb-4">
        <div className="text-xl font-bold">Cuerpo del mensaje</div>
        <div>{decodeHtmlEntities(message.body)}</div>
      </div>

      {message.deal && (
        <div>
          <NavLink to={`/${AppPath.Deals}/view/${message.deal}`} className={'text-blue-500 font-bold text-2xl'}>
            {'Ir al negocio.'}
          </NavLink>
        </div>
      )}

      {!(message.discardedAt ?? message.deal) && (
        <>
          <FormFields fields={fields} handleInputChange={handleInputChange} />

          <div className="text-xl font-bold">Consulta </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 mb-4 ">
            <CheckboxField
              name="inquiry"
              value={''}
              label={'Marcar como consulta'}
              checked={formData?.inquiry ?? false}
              handleInputChange={handleCheckClick}
            />
          </div>
          <div className="flex justify-center">
            <ButtonAction label={'Modificar'} disabled={disabled} handleButtonClick={onModifyClick} />
          </div>

          <div className="mt-2 p-2 mx-auto shadow-md border">
            <ButtonMenuSet
              captions={buttonCaptions}
              short={true}
              clickedIndex={clickedIndex}
              handleButtonClick={(n) => handleMenuClick(n)}
            />

            <div className="mt-4">
              {clickedIndex === 0 && <AsignarUsuario users={users} messageId={formData._id} onRefresh={onRefreshEx} />}
              {clickedIndex === 1 && (
                <div>
                  <ProcesarMensaje messageId={formData._id} />
                </div>
              )}
              {clickedIndex === 2 && <DescartarMensaje messageId={formData._id} onRefresh={onRefreshEx} />}
            </div>
          </div>
        </>
      )}
      <div>
        {errorPostMessage?.message}
        {errorUserProfile?.message}
      </div>
    </div>
  )
}

const AsignarUsuario: React.FC<{
  users: UserProfileDto[]
  messageId: string
  onRefresh: () => void
}> = ({ users, messageId, onRefresh }) => {
  const [selectedUser, setSelectedUser] = useState<string>('')
  const [disabled, setDisabled] = useState<boolean>(true)
  const { postMessageAssign, errorPostMessageAssign } = useMessagePoster()

  const handleInputChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const { value } = e.target

    setDisabled(value === '')
    setSelectedUser(value)
  }

  const onAssignClick = async () => {
    if (disabled) return

    const result = await postMessageAssign(messageId, selectedUser)

    if (result) {
      onRefresh()
    }
  }

  return (
    <div className="mb-4">
      <div>Elige a alguien en tu organización para asignarle este mensaje.</div>
      <SelectUserField
        label="Ten en cuenta que solo podrás asignarlo una vez"
        name="users"
        value={selectedUser}
        options={users}
        handleInputChange={handleInputChange}
      />
      <div className="flex justify-center mt-2">
        <ButtonAction label="Asignar" disabled={disabled} handleButtonClick={onAssignClick} />
      </div>
      <div>{errorPostMessageAssign?.message}</div>
    </div>
  )
}

const DescartarMensaje: React.FC<{ messageId: string; onRefresh: () => void }> = ({ messageId, onRefresh }) => {
  const [disabled, setDisabled] = useState<boolean>(true)
  const [descarta, setDescarta] = useState<boolean>(false)
  const { postMessageDiscard, errorPostMessageDiscard } = useMessagePoster()

  // Handle checkbox change
  const handleDescartarClick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target

    setDescarta(checked)
    setDisabled(!checked)
  }

  const onDiscardClick = async () => {
    if (disabled) return

    const result = await postMessageDiscard(messageId)

    if (result) {
      onRefresh()
    }
  }

  return (
    <div>
      <div className="text-xl font-bold">Descartar Mensaje</div>
      <div>Al descartar el mensaje, no podrá ser procesado.</div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 mb-4 ">
        <CheckboxField
          name="discard"
          value={''}
          label={'Estoy seguro de descartar este mensaje'}
          checked={descarta}
          handleInputChange={handleDescartarClick}
        />
      </div>
      <ButtonAction label={'Descartar'} disabled={disabled} handleButtonClick={onDiscardClick} />
      <div>{errorPostMessageDiscard?.message}</div>
    </div>
  )
}

const ProcesarMensaje: React.FC<{ messageId: string }> = ({ messageId }) => {
  const [disabled, setDisabled] = useState<boolean>(true)
  const [descarta, setDescarta] = useState<boolean>(false)
  const navigate = useNavigate()
  // Handle checkbox change
  const handleProcesarClick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target

    setDescarta(checked)
    setDisabled(!checked)
  }

  const onProcessClick = async () => {
    if (disabled) return

    navigate(`/${AppPath.Messages}/process/${messageId}`)
  }

  return (
    <div>
      <div className="text-xl font-bold">Procesar Mensaje</div>
      <div>Al Procesar el mensaje, quedará asociado a un negocio.</div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 mb-4 ">
        <CheckboxField
          name="discard"
          value={''}
          label={'Estoy seguro de procesar este mensaje'}
          checked={descarta}
          handleInputChange={handleProcesarClick}
        />
      </div>
      <ButtonAction label={'Procesar'} disabled={disabled} handleButtonClick={onProcessClick} />
    </div>
  )
}

export default MessageDetail
